import type { BotExamLangTranslationQuestionViewModel } from '@/shared/api/generated/types/BotExamLangTranslationQuestionViewModel';
import type { RevisionTranslationQuestion } from '../types/revision';
import { mapQuestionType } from './questionType';

/**
 * Converte uma questão de tradução (`BotExamLangTranslationQuestionViewModel`)
 * para o modelo de exibição `RevisionTranslationQuestion`, pareando o texto
 * original (pt-BR) com o traduzido tanto no enunciado quanto nas alternativas
 * (`options[].originalLabel` / `options[].label`).
 *
 * @param dto - Questão de tradução retornada pela API
 * @param index - Índice (1-based) da questão na paginação server-side
 * @returns Questão no formato de exibição da tela de tradução
 */
export function toRevisionTranslationQuestion(
  dto: BotExamLangTranslationQuestionViewModel,
  index: number,
): RevisionTranslationQuestion {
  return {
    id: dto.questionId ?? index,
    index,
    questionType: mapQuestionType(dto.type),
    originalLabel: dto.originalLabel ?? '',
    translatedLabel: dto.label ?? '',
    isReviewed: dto.isReviewed ?? false,
    alternatives: (dto.options ?? []).map((option, optionIndex) => ({
      id: option.id ?? optionIndex,
      originalText: option.originalLabel ?? '',
      translatedText: option.label ?? '',
    })),
  };
}

/**
 * Monta o payload de persistência da tradução de uma questão
 * (`BotExamLangTranslationQuestionViewModel`) para o `PUT /api/v1/exam/revision/question`,
 * combinando o estado editado (enunciado e alternativas traduzidos, revisado) com
 * os campos não editáveis na tela (explicação, trecho, originais, tipo) vindos do
 * DTO original carregado.
 *
 * @param dto - Questão de tradução original retornada pela API
 * @param edited - Questão de exibição com as edições do operador aplicadas
 * @param botExamLangId - ID do idioma da prova cuja tradução está sendo salva
 * @returns Payload pronto para o `PUT /question`
 */
export function toTranslationSavePayload(
  dto: BotExamLangTranslationQuestionViewModel,
  edited: RevisionTranslationQuestion,
  botExamLangId: number,
): BotExamLangTranslationQuestionViewModel {
  return {
    ...dto,
    classroomDisciplineBotExamLangId: botExamLangId,
    label: edited.translatedLabel,
    isReviewed: edited.isReviewed,
    options: (dto.options ?? []).map((option, optionIndex) => {
      const editedAlt = edited.alternatives.find(alt => alt.id === (option.id ?? optionIndex));
      return {
        ...option,
        label: editedAlt?.translatedText ?? option.label ?? null,
      };
    }),
  };
}
